import axios from "axios";
import type { AxiosProgressEvent } from "axios";

type GoogleImageUploadOptions = {
  signal?: AbortSignal;
  onUploadProgress?: (event: AxiosProgressEvent) => void;
};

export type GoogleGalleryImage = {
  link: string;
  title?: string;
  displayLink?: string;
  mime?: string;
  image?: { thumbnailLink?: string; width?: number; height?: number };
};

export async function searchGoogleGalleryImages(query: string, page: number, signal: AbortSignal) {
  const key = process.env.REACT_APP_GOOGLE_CUSTOM_SEARCH_API_KEY || "AIzaSyDBGKDuYYvI1DRbCjvrtnCRasnGukhYOes";
  const cx = process.env.REACT_APP_GOOGLE_CUSTOM_SEARCH_CX || "30dca0bba2d8b4b27";
  const params = new URLSearchParams({
    q: query.trim(), cx, key, searchType: "image", num: "10", start: String((page - 1) * 10 + 1),
    rights: "cc_publicdomain|cc_attribute|cc_commercial|cc_nonderived",
  });
  const response = await fetch(`https://www.googleapis.com/customsearch/v1?${params}`, { signal });
  const data = await response.json();
  if (!response.ok) throw new Error(data?.error?.message || "Google image search is unavailable. Please try again.");
  const items: GoogleGalleryImage[] = (Array.isArray(data?.items) ? data.items : []).filter((item: GoogleGalleryImage) =>
    /^https?:\/\//i.test(item.link || "") &&
    (["image/jpeg", "image/png"].includes(item.mime || "") || /\.(jpe?g|png)(?:[?#]|$)/i.test(item.link)),
  );
  return { items, hasNextPage: page < 10 && Boolean(data?.queries?.nextPage?.length || items.length === 10) };
}

export async function uploadGoogleImageToGallery(
  formData: FormData,
  options: GoogleImageUploadOptions = {},
) {
  const token = axios.defaults.headers.common.Authorization;
  if (!token) {
    throw new Error("Authorization token is missing. Please sign in again.");
  }

  try {
    return await axios.post("/consent_clause/upload_google_image_to_s3", formData, {
      headers: { Authorization: token },
      timeout: 120_000,
      signal: options.signal,
      onUploadProgress: options.onUploadProgress,
    });
  } catch (error) {
    const failure = error as {
      code?: string;
      response?: { data?: { detail?: unknown } };
    };

    if (failure?.code === "ERR_CANCELED") throw error;
    if (failure?.code === "ECONNABORTED" || failure?.code === "ETIMEDOUT") {
      throw new Error("The image upload timed out. Check your private gallery before trying again.");
    }

    const detail = failure?.response?.data?.detail;
    throw new Error(
      typeof detail === "string" && detail.trim()
        ? detail
        : "Failed to add the selected image. Please try another image.",
    );
  }
}
